import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { MapPin, Phone, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Home = () => {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchListings();
  }, []);

  const fetchListings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/listings`);
      setListings(response.data);
    } catch (error) {
      toast.error('Failed to fetch listings');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="home-page">
        <div className="text-lg">Loading marketplace...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-primary/5 to-secondary/5 py-16 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 text-primary">
            Premium Country Chickens
          </h1>
          <p className="text-xl md:text-2xl text-neutral-700 mb-8 max-w-3xl mx-auto">
            Buy and sell healthy, farm-raised country chickens from trusted sellers in your area
          </p>
        </div>
      </div>

      {/* Listings Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {listings.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-xl text-neutral-600">No listings available yet. Be the first to post!</p>
          </div>
        ) : (
          <div className="marketplace-grid">
            {listings.map((listing) => (
              <Card 
                key={listing.id} 
                className="listing-card overflow-hidden border-neutral-200 hover:shadow-lg"
                data-testid="listing-card"
              >
                <div className="image-preview-container">
                  <img 
                    src={listing.image_url} 
                    alt={listing.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                
                <CardHeader>
                  <CardTitle className="text-xl" style={{ fontFamily: 'Young Serif, serif' }}>
                    {listing.title}
                  </CardTitle>
                  <CardDescription className="text-base font-medium text-secondary">
                    {listing.breed}
                  </CardDescription>
                </CardHeader>
                
                <CardContent className="space-y-4">
                  <p className="text-neutral-700 line-clamp-2">{listing.description}</p>
                  
                  <div className="space-y-2">
                    <div className="flex items-center text-sm text-neutral-600">
                      <MapPin className="w-4 h-4 mr-2 text-primary" />
                      {listing.location}
                    </div>
                    <div className="flex items-center text-sm text-neutral-600">
                      <Phone className="w-4 h-4 mr-2 text-primary" />
                      {listing.contact}
                    </div>
                    {listing.age && (
                      <div className="flex items-center text-sm text-neutral-600">
                        <Calendar className="w-4 h-4 mr-2 text-primary" />
                        Age: {listing.age}
                      </div>
                    )}
                  </div>

                  {listing.weight && (
                    <div className="text-sm">
                      <span className="font-medium text-neutral-700">Weight:</span> {listing.weight}
                    </div>
                  )}

                  {listing.vaccination_details && (
                    <div className="text-sm bg-primary/5 p-3 rounded-lg">
                      <span className="font-medium text-primary">Vaccination:</span>
                      <p className="text-neutral-700 mt-1">{listing.vaccination_details}</p>
                    </div>
                  )}

                  <div className="pt-4 border-t">
                    <div className="text-3xl font-bold text-primary" style={{ fontFamily: 'Young Serif, serif' }}>
                      ₹{listing.cost.toFixed(2)}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Home;